<template>
  <div v-if="model.status === TokenStatus.Active" class="w-10">
    <loader :is-loading="isDeletingToken" />

    <action-menu-wrapper-v2
      data-cy="action-menu"
      :items="menuItems"
      teleport
      check-for-overflow
      close-menu-on-trigger-hide
    />

    <confirm-modal
      v-if="showRevokeModal"
      :title="$t('EMPLOYEES.ACCESS_TOKENS.REVOKE_TITLE')"
      :description="$t('EMPLOYEES.ACCESS_TOKENS.REVOKE_DESCRIPTION')"
      :confirm-button="$t('COMMON.REVOKE')"
      :close="() => (showRevokeModal = false)"
      confirm-button-type="error"
      @confirm="removeToken"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { Ability, PermissionSubject } from "@/enums/auth";
import { usePermissions } from "@/hooks/auth";
import { useAccessTokenDeleteMutation } from "@/hooks/queries/accessTokens";
import { ROUTE_ACCESS_TOKENS_EDIT } from "@/router/routes";
import { TokenStatus } from "@/schemas/accessTokens";

import IconEdit from "@/components/icons/IconEdit.vue";
import IconTrashCanOutline from "@/components/icons/IconTrashCanOutline.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapperV2 from "@/components/ui/ActionMenuWrapperV2.vue";
import Loader from "@/components/ui/Loader.vue";

import type { MenuItem } from "@/components/ui/ActionMenuWrapperV2.vue";
import type { AccessToken } from "@/schemas/accessTokens";

const props = defineProps<{ model: AccessToken }>();

const { t } = useI18n();
const router = useRouter();
const { canPerformActionReactive } = usePermissions();

const { mutate: deleteToken, isPending: isDeletingToken } =
  useAccessTokenDeleteMutation();

const canRevokeToken = canPerformActionReactive(
  PermissionSubject.accessToken,
  Ability.revoke,
  { accessToken: props.model.id }
);

const canUpdateToken = canPerformActionReactive(
  PermissionSubject.accessToken,
  Ability.update,
  { accessToken: props.model.id }
);

const showRevokeModal = ref(false);

const removeToken = () => {
  deleteToken(props.model.id);
  showRevokeModal.value = false;
};

const navigateToEdit = () => {
  router.push({
    name: ROUTE_ACCESS_TOKENS_EDIT,
    params: { tokenId: props.model.id },
  });
};

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [];

  if (canUpdateToken.value) {
    items.push({
      label: t("COMMON.EDIT"),
      handler: navigateToEdit,
      icon: IconEdit,
    });
  }

  if (canRevokeToken.value) {
    items.push({
      label: t("COMMON.REVOKE"),
      handler: () => (showRevokeModal.value = true),
      icon: IconTrashCanOutline,
      color: "red",
    });
  }

  return items;
});
</script>
